import {
  AdvancedMarker,
  APIProvider,
  ControlPosition,
  Map,
  MapControl,
  RenderingType,
  useMap,
} from "@vis.gl/react-google-maps";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { getUrl, radiusZoomLevel } from "../../utils/staticMethods";
import MapDetail from "../detail/MapDetail";
import CommonMapItems from "./CommonMapItems";
import useAuth from "../../hooks/useAuth";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import * as queryString from "query-string";
import axios from "axios";
import api from "../../api";
import CommonMapDefaultItem from "./CommonMapDefaultItem";
import GoogleSearch from "./GoogleSearch";
import TrackingSearch from "./TrackingSearch";
import ButtonsControl from "./ButtonsControl";
import CommonMapTrafficLayer from "./CommonMapTrafficLayer";
import useCommonMap from "../../hooks/useCommonMap";
import CommonMapFloorplans from "./CommonMapFloorplans";
import useSrlHelper from "../../hooks/useSrlHelper";
import { imageSet } from "../../pages/icons/AssetIcon/IconList";
import defaultImg from "../../assets/img/icon/Aircraft/Airliner_Black.png";
import ReaderSelector from "../../pages/readers/ReaderSelector";
import { Card } from "react-bootstrap";
import { Grid, Link } from "react-feather";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeartbeat } from "@fortawesome/free-solid-svg-icons";
import moment from "moment-timezone";

const thingsArr = {
  asset: "assets",
  person: "people",
  zone: "zones",
  assets: "assets",
  people: "people",
  zones: "zones",
};

const CommonMapInitializer = () => {
  const commonMap = useCommonMap();
  const map = useMap();
  useEffect(() => {
    if (commonMap && map) {
      commonMap.setMap(map);
    }
  }, [commonMap, map]);
  return null;
};

const CommonMap = forwardRef(
  (
    {
      height,
      type = null,
      id = null,
      data = null,
      center = null,
      showMapDetail = false,
      showGoogleSearch = false,
      showStreetView = true,
      showTrackingSearch = false,
      showNearByAssets = false,
      showNearByZones = false,
      showAllLabels = false,
      showHashTagFilter = false,
      showCategoryFilter = false,
      showTrafficLayer = false,
      showReaders = false,
      floorplanMode = "",
      onFloorplanChanged,
      onSelectedReaderChanged,
      onSelectedGeometryChanged,
      onMapReady,
      children,
    },
    ref
  ) => {
    const commonMap = useCommonMap();
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { reportid } = queryString.parse(location.search);
    const [report, setReport] = useState();
    const [hashtag, setHashtag] = useState(null);
    const [category, setCategory] = useState(null);
    const [following, setFollowing] = useState(false);
    const follow = useRef(following);
    const showAllRef = useRef(false);
    const [firstLoad, setFirstLoad] = useState(false);
    const [overlay, setOverlay] = useState(false);
    const [current, setCurrent] = useState(null);
    const [nearbyItems, setNearbyItems] = useState([]);
    const [mapParams, setMapParams] = useState({});
    const [selectedFloorplan, setSelectedFloorplan] = useState(null);
    const [readers, setReaders] = useState([]);
    const [selectedReader, setSelectedReader] = useState(null);
    const [showReader, setShowReader] = useState(false);

    useImperativeHandle(ref, () => ({
      showFindAddress: () => {},
      getMap: () => commonMap && commonMap.map,
      getCenter: () => commonMap && commonMap.map && commonMap.map.getCenter(),
      setSelectedFloorplan: handleSelectedFloorplanChanged,
    }));

    const onSelectReader = (reader) => {
      setSelectedReader(reader);
      onSelectedReaderChanged && onSelectedReaderChanged(reader);
    };

    useEffect(() => {
      setCurrent((prevState) => {
        if (prevState) {
          if (
            following &&
            prevState.lastPosition &&
            (prevState.lastPosition.latitude !== data.lastPosition.latitude ||
              prevState.lastPosition.longitude !== data.lastPosition.longitude)
          ) {
            return data;
          }
          return prevState;
        } else {
          return data;
        }
      });
    }, [data]);

    const handleCurrentChanged = ({ current, marker }) => {
      // Only set current if we have not been passed data
      if (!data) {
        setCurrent(current);
      }
    };

    const handleSelectedFloorplanChanged = (floorplan) => {
      setSelectedFloorplan(floorplan);
      console.log("Selected Floorplan Changed:", floorplan);
      onFloorplanChanged && onFloorplanChanged(floorplan);
    };

    const handleSelectedGeometryChanged = (geometry) => {
      console.log("Selected Geometry Changed:", geometry);
      onSelectedGeometryChanged && onSelectedGeometryChanged(geometry);
    };

    useEffect(() => {
      let map = commonMap && commonMap.map;
      if (!commonMap?.map || !center) return;
      center && commonMap.setCenter(center);
      onMapReady && onMapReady(map);
    }, [commonMap?.map, center]);

    const handleMapChange = useCallback(
      (e) => {
        if (commonMap) {
          let map = e.map;
          let center = map.getCenter();
          let zoom = map.getZoom();
          commonMap.setMap(map);
          commonMap.setCenter(center);
          commonMap.setZoom(zoom);
        }
        if (
          (showNearByAssets === true || showNearByZones === true) &&
          setMapParams
        ) {
          console.log("mapChange", e);
          var map = e.map;
          var center = map.getCenter();
          var zoom = map.getZoom();
          console.log("map changed", { center, zoom });
          setMapParams((prev) => {
            var update = {
              latitude: center.lat(),
              longitude: center.lng(),
              radius: radiusZoomLevel[Math.round(zoom)],
            };
            if (
              prev.latitude &&
              prev.latitude === update.latitude &&
              prev.longitude &&
              prev.longitude === update.longitude &&
              prev.radius &&
              prev.radius === update.radius
            ) {
              return prev;
            } else {
              console.log("prev->update", { prev, update });
              return Object.assign({}, prev, update);
            }

            // loadNearby();
          });
        }
      },
      [commonMap, setMapParams]
    );

    const loadNearby = useCallback(
      throttleAsync(() => {
        (async () => {
          var r = async () => {
            let source = axios.CancelToken.source();
            try {
              var url = reportid ? "maps/nearbyreport" : "maps/nearby";
              var getParams = mapParams;
              if (reportid) {
                getParams.reportid = reportid;
              }
              getParams.hashtag = hashtag;
              getParams.category = category;
              getParams.limit = 16000;
              var res = await api({
                method: "GET",
                url: url,
                params: getParams,
                cancelToken: source.token,
              });

              const totalHeader = res.headers["x-total-count"];
              if (Number(totalHeader) >= getParams.limit) {
                setNearbyItems([]);
                // count[0] = Number(totalHeader);
                setOverlay(true);
              } else {
                setNearbyItems([].concat(res.data));
                setOverlay(false);
              }
              return 0;
            } catch (ex) {
              console.warn("load", ex);
              return -1;
            } finally {
              if (source) {
                source.cancel("unmount");
              }
            }
          };
          await r();
        })();
      }, 200),
      [reportid, mapParams]
    );

    const handleAll = () => {
      if (nearbyItems && Array.isArray(nearbyItems) && nearbyItems.length > 0) {
        let shown = nearbyItems.find((item) => item.showInfo === true);
        if (shown) {
          showAllRef.current = false;
        } else {
          showAllRef.current = true;
        }
        let newItems = nearbyItems.map((item) => {
          if (item.type !== "zone" && item.type !== "zones") {
            item.showInfo = showAllRef.current;
          }
          return item;
        });
        setNearbyItems(newItems);
        // let newArr = arr.map((item) => {
        //   let obj = { lastPosition: item.ids[0] };
        //   return obj;
        // });
        // setInfoItem(newArr);
      }
    };

    const getDefaultItem = useCallback(async () => {
      // If we have the data param set, then don't get default
      if (!data) {
        if (reportid) {
          let defaultResult = await api.get(`maps/defaultitemreport`, {
            params: {
              reportid: reportid,
            },
          });

          let defaultResultData = defaultResult.data;
          if (!defaultResultData) {
            setCurrent(null);
            setNearbyItems([]);
          } else {
            defaultResultData.type = thingsArr[defaultResultData.type];
            let res = await api.get(
              `${defaultResultData.type}/${defaultResultData.id}`
            );

            let cur = res.data;
            cur.type = defaultResultData.type;
            setCurrent(cur);
          }
        } else {
          let defaultResult = await api.get(`maps/defaultitem`, {
            params: {
              hashtag: hashtag,
              category: category,
            },
          });

          let defaultResultData = defaultResult.data;
          if (!defaultResultData) {
            setCurrent(null);
            setNearbyItems([]);
          } else {
            defaultResultData.type = thingsArr[defaultResultData.type];
            let res = await api.get(
              `${defaultResultData.type}/${defaultResultData.id}`
            );
            let cur = res.data;
            cur.type = defaultResultData.type;
            setCurrent(cur);
          }
        }
      }
    }, [data, reportid, hashtag, category, setCurrent, setNearbyItems]);

    useEffect(getDefaultItem, [getDefaultItem]);

    useEffect(() => {
      if (type && user && !user[type]) {
        navigate("/");
        return;
      }
    }, [user, type]);

    useEffect(() => {
      let cancelled = false;

      (async () => {
        if (reportid) {
          setReport(undefined);

          try {
            const reportResult = await api.get(`reports/${reportid}`);

            if (!cancelled && reportResult.status === 200) {
              setReport(reportResult.data);
            }
          } catch (err) {
            if (!cancelled) {
              console.error("Failed to load report:", err);
            }
          }
        }
      })();

      return () => {
        cancelled = true;
        setReport(undefined);
      };
    }, [reportid, setReport]);

    useEffect(() => {
      if (showNearByAssets || showNearByZones) {
        console.log("loading");
        var intervalId = setInterval(() => {
          // loadNearbyThrottled();
          loadNearby();
        }, 5000);

        loadNearby();
      }

      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    }, [loadNearby]);

    useEffect(() => {
      if (current) {
        setMapParams((prev) => {
          return Object.assign({}, prev, {
            latitude: current.latitude,
            longitude: current.longitude,
            // radius: radiusZoomLevel[Math.round(map.getZoom())],
          });
        });
      }
    }, [current]);

    function throttleAsync(fn, wait) {
      let lastRun = 0;
      let timeout;
      let running = false;

      return async function (...args) {
        const now = Date.now();

        const run = async () => {
          running = true;
          lastRun = now;
          try {
            await fn.apply(this, args);
          } finally {
            running = false;
          }
        };

        if (running || now - lastRun < wait) {
          clearTimeout(timeout);
          timeout = setTimeout(run, Math.max(wait - (now - lastRun), 0));
        } else {
          await run();
        }
      };
    }

    return (
      <React.Fragment>
        <div style={{ width: "100%" }}>
          {overlay && (
            <div className="p-3 ps-2 bg-light text-warning">
              Too many items to show. Please zoom or search to narrow results
            </div>
          )}
          {!overlay && !current && firstLoad && (
            <div className="p-3 ps-2 bg-light text-warning">
              No relevant assets/people to show, please select other
              hashtag/category
            </div>
          )}
          {showMapDetail && current ? (
            <MapDetail
              profile={current}
              following={following}
              onChangeToggle={(e) => {
                follow.current = e.target.checked;
                setFollowing(e.target.checked);
              }}
            />
          ) : null}
        </div>
        {selectedReader && (
          <Card className="mb-0 border-bottom">
            <Card.Body className="d-flex align-items-center ">
              <NavLink
                to={`/readers/edit?id=${selectedReader.id}&direct=floorplans&fId=${selectedFloorplan?.id}`}
              >
                {selectedReader.serial}
              </NavLink>
              {selectedReader.wirepasNetworkId && (
                <Grid className="ms-2" size={14} />
              )}
              {selectedReader.wirepas && <Link className="ms-1" size={14} />}
              {selectedReader.lastReport && (
                <div className="ms-2">
                  <FontAwesomeIcon icon={faHeartbeat} size={"sm"} />
                  <span className="ms-2">
                    {moment(selectedReader.lastReport).format(
                      "DD/MM/YYYY HH:mm:ss"
                    )}
                  </span>
                </div>
              )}
              {(!selectedReader.latitude || !selectedReader.longitude) && (
                <div className="ms-2">
                  <span className="ms-2 text-danger">Not Yet Configured</span>
                </div>
              )}
            </Card.Body>
          </Card>
        )}
        <div
          style={{ width: "100%", height: `${height}` }}
          className={`${selectedFloorplan ? "hidden-map" : ""}`}
        >
          <Map
            reuseMaps={false}
            defaultCenter={{ lat: 55.0, lng: -2.7 }}
            defaultZoom={6}
            mapId={"90f87356969d889c"}
            gestureHandling="cooperative"
            renderingType={RenderingType.VECTOR}
            mapTypeControlOptions={{
              position: ControlPosition.LEFT_BOTTOM,
            }}
            onIdle={handleMapChange}
            clickableIcons={false}
            tiltInteractionEnabled={false}
            headingInteractionEnabled={false}
            zoomControl={false}
            cameraControl={true}
            streetViewControl={showStreetView}
            fullscreenControl={true}
            // onMapTypeIdChanged={handleMapChange}
            // onZoomChanged={handleMapChange}
            // onCenterChanged={handleMapChange}
            // onDragend={handleMapChange}
          >
            <CommonMapInitializer />
            {showGoogleSearch && <GoogleSearch />}
            {showTrackingSearch && <TrackingSearch report={report} />}
            <ButtonsControl
              report={report}
              nearbyItems={nearbyItems}
              hashtag={hashtag}
              setHashtag={setHashtag}
              category={category}
              setCategory={setCategory}
              handleAll={handleAll}
              showNearByAssets={showNearByAssets}
              showAllLabels={showAllLabels}
              showHashTagFilter={showHashTagFilter}
              showCategoryFilter={showCategoryFilter}
            />

            {!data && (
              <CommonMapDefaultItem
                current={current}
                onCurrentChanged={handleCurrentChanged}
              />
            )}
            {showReaders && (
              <MapControl position={ControlPosition.TOP_LEFT}>
                <div
                  style={{
                    position: "relative",
                    background: "#ffffffff",
                    margin: "10px",
                  }}
                >
                  <ReaderSelector
                    reader={selectedReader}
                    getAllReaders={(e) => setReaders(e)}
                    onSelect={onSelectReader}
                    showDefault
                    showReader={showReader}
                  />
                </div>
              </MapControl>
            )}
            {data &&
              data.lastPosition &&
              data.lastPosition.longitude &&
              data.lastPosition.latitude && (
                <GeoMarker
                  item={data}
                  img={
                    imageSet[getUrl(data.icon)]
                      ? require("../../assets/img/icon/" +
                          imageSet[getUrl(data.icon)]).default
                      : defaultImg
                  }
                  lat={Number(data.lastPosition.latitude)}
                  lng={Number(data.lastPosition.longitude)}
                />
              )}
            {(showNearByAssets || showNearByZones) && (
              <CommonMapItems
                showNearByAssets={showNearByAssets}
                showNearByZones={showNearByZones}
                nearbyItems={nearbyItems}
                onCurrentChanged={handleCurrentChanged}
                reportId={reportid}
                report={report}
              />
            )}
            {/* <CommonMapFloorplans
              mode={floorplanMode}
              selectedFloorplan={selectedFloorplan}
              onSelectedFloorplanChanged={handleSelectedFloorplanChanged}
              onSelectedGeometryChanged={handleSelectedGeometryChanged}
            /> */}
            {showTrafficLayer && <CommonMapTrafficLayer />}
            {children}
          </Map>
        </div>
      </React.Fragment>
    );
  }
);

const GeoMarker = ({ item, img, lat, lng, plan }) => {
  const { itemDisplayName } = useSrlHelper();
  const map = useMap();

  useEffect(() => {
    if (lat && lng && map) {
      map.setZoom(18);
      map.panTo({ lat: lat, lng: lng });
    }
  }, [lat, lng, map]);

  var displayName = itemDisplayName(item);
  const imageSrc = imageSet[getUrl(item.icon)]
    ? require("../../assets/img/icon/" + imageSet[getUrl(item.icon)]).default
    : defaultImg;
  return (
    <React.Fragment>
      {imageSrc && (
        <AdvancedMarker
          position={{
            lat,
            lng,
          }}
          style={{ transform: "translate(0, 12px)" }}
        >
          <div className="d-flex flex-column align-items-center">
            <img src={imageSrc} width={"24px"} alt="Tracked Item Marker" />
            <div className="mt-4 px-2 text-nowrap text-break text-white position-absolute bg-black bg-opacity-25">
              {displayName}
            </div>
          </div>
        </AdvancedMarker>
      )}
    </React.Fragment>
  );
};
export default CommonMap;
